import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAtom } from 'jotai'
import { cartListAtom, userAtom } from '../store'
import { fetchShopifyProductByID, isUnavailable, resolveSellerIdentity } from '../firebase/shopify'
import Breadcrumb from '../components/Breadcrumb'
import DialogLoading from '../components/dialogs/DialogLoading'
import { getBlockedSellerIds } from '../firebase/blocks'
import BlockSellerButton from '../components/commons/BlockSellerButton'

const ShopifyProductDetails = () => {
  const { productId } = useParams()
  const [card, setCard] = useState(null)
  const [loadStatus, setLoadStatus] = useState('ok')
  // The account behind the store, resolved on load rather than read off the
  // product -- the product's seller is the store, whose owner can change.
  // null until resolved. Starting with an empty name made the page flash the
  // generic fallback on every load before the store's owner came back.
  const [seller, setSeller] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeImage, setActiveImage] = useState(null)
  const [openDelivery, setOpenDelivery] = useState(false)
  const [openPayments, setOpenPayments] = useState(false)
  const [cartList, setCartList] = useAtom(cartListAtom)
  const [userData] = useAtom(userAtom)
  // Whether the shopper has blocked the store's owner. The page still opens
  // on a direct link, the way a switched-off listing does, but there is
  // nothing to buy on it. Blocking and unblocking happen on the owner's
  // profile, not here.
  const [sellerBlocked, setSellerBlocked] = useState(false)

  useEffect(() => {
    const loadProduct = async () => {
      const { product, status } = await fetchShopifyProductByID(productId)
      setCard(product)
      setLoadStatus(status)
      if (product) {
        resolveSellerIdentity(product).then(setSeller)
      }
      setActiveImage(product?.pictures?.[0] || '/assets/images/image_item_default.png')
      setLoading(false)
    }

    loadProduct()
    scrollTo({ top: 0 })
  }, [productId])

  // seller.uid is the account that owns the store, which is what a buyer
  // blocks; card.seller_id is the store's role id and is never blocked
  // directly.
  useEffect(() => {
    if (!userData?.id || !seller?.uid) {
      setSellerBlocked(false)
      return
    }

    getBlockedSellerIds(userData.id).then((ids) => setSellerBlocked(ids.has(seller.uid)))
  }, [userData?.id, seller?.uid])

  const toCapitalize = (str) => {
    if (str && str[0]) return str[0].toUpperCase() + str.slice(1, str.length).toLowerCase()
    return str
  }

  const listingIsInCart = () => {
    if (!card) return false
    return cartList.some((item) => item.id === card.id)
  }

  const handleAddToCart = () => {
    if (!card || isUnavailable(card) || sellerBlocked || card.quantity === 0 || listingIsInCart()) return

    setCartList((prev) => {
      if (prev.some((item) => item.id === card.id)) {
        return prev
      }

      return [
        ...prev,
        {
          ...card,
          count: 1,
          managed_by_shopify: true,
          inventory_source: 'shopify',
          merchant_id: card?.merchant_id || card?.seller_id || card?.seller || 'shopify',
          seller_id: card?.seller_id || card?.seller || '',
          seller: card?.seller || card?.seller_id || '',
          seller_email: card?.seller_email || '',
          seller_display_name: card?.seller_display_name || '',
          shopify_variant_id: card.shopify_variant_id || card.variant_id || null,
        }
      ]
    })
  }

  return (
    <div>
      {loading && <DialogLoading onClose={() => {}} />}

      <Breadcrumb link="/shopify" />

      {!loading && !card && (
        <div className="w-full text-center text-white py-20">
          <p className="text-lg">
            {loadStatus === 'error'
              ? "We couldn't load this product."
              : 'This product is no longer available.'}
          </p>
          <p className="text-sm text-[#717171] mt-3">
            {loadStatus === 'error'
              ? 'Something went wrong reaching the store. Please try again.'
              : 'It may have been removed from the store, or the link may be out of date.'}
          </p>
          <p className="text-xs text-[#4a4a4a] mt-4 font-mono break-all">{productId}</p>
          {/* /shopify is a static placeholder that lists nothing; the
              marketplace is where Shopify items actually appear. */}
          <Link
            to="/marketplace/categories"
            className="inline-block mt-8 uppercase text-sm bg-white px-8 py-3 text-black hover:bg-primary hover:text-white transition-all"
          >
            Browse the marketplace
          </Link>
        </div>
      )}

      {!loading && card && (
        <div className="flex flex-col lg:flex-row items-start gap-6 lg:gap-12">
          <div className="w-full grid grid-cols-1 lg:grid-cols-5 gap-5">
            <div className="flex flex-col gap-y-4 max-lg:!hidden">
              {(card.pictures || []).slice(0, 5).map((image) => (
                <div key={`thumb-${image}`} className="px-[2px] h-[20%] lg:px-1">
                  <div
                    className={`relative cursor-pointer h-full w-full ${activeImage === image ? '!opacity-100 !border-primary' : 'opacity-40'}`}
                    onClick={() => setActiveImage(image)}
                  >
                    <img
                      src={image}
                      className="absolute top-0 left-0 w-full object-cover h-full bg-white border-primary border-[1px]"
                      alt={`Thumbnail ${image}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="product-ask-section w-full pt-[100%] lg:col-span-4">
              <img
                src={activeImage}
                className="absolute top-0 w-full h-full object-cover bg-white border-primary border-[1px]"
                alt="Main Displayed"
              />
            </div>

            <div className="grid grid-cols-5 sm:gap-x-4 gap-x-1 !w-full lg:!hidden">
              {(card.pictures || []).map((image) => (
                <div key={`thumb-mobile-${image}`} className="px-[2px] lg:px-1">
                  <div
                    className={`relative cursor-pointer w-full pt-[100%] ${activeImage === image ? '!opacity-100 !border-primary' : 'opacity-40'}`}
                    onClick={() => setActiveImage(image)}
                  >
                    <img
                      src={image}
                      className="absolute top-0 left-0 w-full object-cover h-full bg-white border-primary border-[1px]"
                      alt={`Thumbnail ${image}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full px-4 py-5 border-[#434343] border-[1px]">
            <h1 className="uppercase text-white font-bold leading-[1.2] text-[20px] lg:text-[20px]">
              {card.title}
            </h1>

            <hr className="my-4 border-gray" />

            <div className="w-full md:flex items-center justify-between mt-2 lg:mt-0">
              <div>
                <div className="flex items-center text-primary text-[20px] lg:text-[20px] font-aeonik font-bold">
                  ${Number(card.price || 0).toFixed(2)}
                </div>
              </div>

              <span className="inline-flex items-center px-3 py-1 text-[10px] uppercase border border-primary text-primary bg-[#1AB6F920] rounded-[12px]">
                Shopify Managed
              </span>
            </div>

            <hr className="my-4 border-gray" />

            <span className="uppercase text-primary text-[12px]">Description:</span>

            <p className="text-white text-sm font-thin mt-4 lg:mt-0">{card.description}</p>

            <div className="w-full border border-[#FFFFFF]/20 p-3 lg:p-5 mt-8">
              <div className="w-full flex items-center justify-between pb-3 border-b border-b-[#fff]/20">
                <span className="font-aero uppercase text-base lg:text-xl text-white">
                  Sold by
                </span>
                <div className="flex flex-col items-end">
                  <span className="text-primary text-sm lg:text-base">
                    {seller ? (seller.displayName || 'Shopify Store') : ''}
                  </span>
                  {seller?.uid && (
                    <div className="flex items-center gap-3">
                      <Link
                        className="text-[10px] lg:text-xs flex items-center text-primary underline"
                        to={`/user-profile/${seller?.uid}`}
                      >
                        View Profile
                      </Link>

                      {/* Blocks the ACCOUNT that owns the store, so one block
                          covers every store they run. card.seller_id is the
                          store's role id and has no profile to block. */}
                      <BlockSellerButton
                        variant="link"
                        seller={{ id: seller.uid, displayName: seller.displayName }}
                        onChange={setSellerBlocked}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Either the store owner took this listing off the marketplace
                or the shopper blocked the seller. The page still opens -- a
                buyer or seller following it from an order or their history
                should see what they bought -- but there is nothing to buy. */}
            {isUnavailable(card) || sellerBlocked ? (
              <div className="mt-6 border border-[#434343] px-4 py-3">
                <span className="uppercase text-sm text-[#717171]">
                  {sellerBlocked
                    ? 'You have blocked this seller'
                    : 'This listing is currently unavailable'}
                </span>
              </div>
            ) : (
              <div className="mt-6 flex gap-3">
                <button
                  className={`uppercase text-sm bg-white px-8 py-3 text-black transition-all ${card.quantity === 0 || listingIsInCart() ? 'opacity-50' : 'hover:bg-primary hover:text-white'}`}
                  onClick={handleAddToCart}
                  disabled={card.quantity === 0 || listingIsInCart()}
                >
                  {listingIsInCart() ? 'Added to Cart' : 'Add to Cart'}
                </button>
              </div>
            )}

            <div className="w-full space-y-4 mt-8">
              <div className="w-full flex">
                <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                  Product Type:
                </span>
                <span className="text-white">{toCapitalize(card.category || 'shopify')}</span>
              </div>
              <div className="w-full flex">
                <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                  Condition:
                </span>
                <span className="text-white">{toCapitalize(card.condition || 'new')}</span>
              </div>
              <div className="w-full flex">
                <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                  Inventory:
                </span>
                <span className="text-white">{card.quantity || 0}</span>
              </div>
              <div className="w-full flex">
                <span className="uppercase text-xs font-bold text-primary w-full max-w-[140px] lg:max-w-[210px] block">
                  Source:
                </span>
                <span className="text-white">Shopify</span>
              </div>
            </div>

            <hr className="my-4 border-gray" />

            <div className="mt-6 ">
              <div
                className="w-full flex justify-between items-center cursor-pointer pb-6 mb-6 border-b border-b-white/20"
                onClick={() => setOpenDelivery(!openDelivery)}
              >
                <span className="text-white font-aero uppercase text-2xl">Delivery & returns</span>
                <img src="/assets/icons/icon-arrow-drop-down.svg" className={`${openDelivery ? 'rotate-180' : ''}`} alt="" />
              </div>

              {openDelivery && (
                <div className="text-white w-full">
                  <div className="w-full space-y-4 mt-6">
                    <div className="w-full flex">
                      <img alt="circle" src="/assets/icons/icon-check-circle.svg" className="w-[25px] mr-3" />
                      <span className="text-xs lg:text-base">Prices include shipping OR add a separate price box for shipping prices</span>
                    </div>
                    <div className="w-full flex">
                      <img alt="circle" src="/assets/icons/icon-check-circle.svg" className="w-[25px] mr-3" />
                      <span className="text-xs lg:text-base">Please allow for 2-4 business days for processing.</span>
                    </div>
                    <div className="w-full flex">
                      <img alt="circle" src="/assets/icons/icon-check-circle.svg" className="w-[25px] mr-3" />
                      <span className="text-xs lg:text-base">For trading cards and sticker items, we have a no-return policy in place.</span>
                    </div>
                    <span className="text-xs lg:text-base block text-primary">1-3 business days</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6">
              <div
                className="w-full flex justify-between items-center cursor-pointer pb-6 mb-4 border-b border-b-white/20"
                onClick={() => setOpenPayments(!openPayments)}
              >
                <span className="text-white font-aero uppercase text-2xl">Payments</span>
                <img src="/assets/icons/icon-arrow-drop-down.svg" className={`${openPayments ? 'rotate-180' : ''}`} alt="" />
              </div>

              {openPayments && (
                <div className="text-white w-full">
                  <p className="text-xs lg:text-base opacity-60">
                    We accept all major credit and debit cards, including Visa, Mastercard, and American Express.
                  </p>
                  <img src="/assets/images/payment_lists.svg" className="mt-5 max-w-[230px] lg:max-w-[unset]" alt="payment methods" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ShopifyProductDetails
